import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Badge, StyledComponentProps, Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../store/reducers/auth';
import useApi from '../../api';
import { AuthState, SettingsState, Store } from '../../store/types';
import { PublicUser } from '../../api/types/user';

interface OnlineBadgeProps extends StyledComponentProps {
    status?: string;
    ripple?: string;
    badge?: string;
    size?: string;
}

function statusToBgColor(theme: Theme, status?: string) {
    switch (status) {
        case 'online':
            return theme.palette.success.main;
        case 'inactive':
            return theme.palette.warning.main;
        default:
            return theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.common.black;
    }
}

const OnlineBadge = styled(Badge)<OnlineBadgeProps>(
    ({ theme, status, ripple = 'false', badge = 'false', size = 'small' }) => ({
        '& .MuiBadge-badge': {
            width: size === 'big' ? 25 : undefined,
            height: size === 'big' ? 25 : undefined,
            borderRadius: 25,
            backgroundColor: Boolean(badge === 'true')
                ? statusToBgColor(theme, status)
                : 'transparent',
            color: Boolean(badge === 'true')
                ? statusToBgColor(theme, status)
                : 'transparent',
            boxShadow: Boolean(badge === 'true')
                ? `0 0 0 2px ${theme.palette.background.paper}`
                : 'none',
            '&::after': Boolean(badge === 'true') && {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                animation: 'ripple 1.2s infinite ease-in-out',
                border: '1px solid currentColor',
                content: '""',
            },
        },
        '@keyframes ripple': {
            '0%': {
                transform: 'scale(.8)',
                opacity: Boolean(ripple === 'true') ? 1 : 0,
            },
            '100%': {
                transform: 'scale(2.4)',
                opacity: 0,
            },
        },
    }),
);

type ActivityBadgeProps = {
    user?: AuthState['user'] | PublicUser;
    children: ReactElement;
    trackUserActivity?: boolean;
    timeout?: number;
    showBadge?: boolean;
    showRipple?: boolean;
    size?: 'small' | 'big';
};

export default function ActivityBadge({
    user,
    children,
    trackUserActivity = false,
    timeout = 30000,
    showBadge = false,
    showRipple = false,
    size = 'small',
}: ActivityBadgeProps) {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const lastUpdateRef = useRef<string>();

    const dispatch = useDispatch();
    const api = useApi();
    const session = useSelector<Store, AuthState>(state => state.auth);
    const { networkError } = useSelector<Store, SettingsState>(
        state => state.settings,
    );

    const [loading, setLoading] = useState(false);

    const isThisUser = user?.id === session?.user?.id;

    const updateStatus = useCallback(
        (status: string, force: boolean = false) => {
            if (
                (!networkError &&
                    !loading &&
                    session.user?.status !== status &&
                    lastUpdateRef.current !== status) ||
                force
            ) {
                setLoading(true);
                lastUpdateRef.current = status;
                api.user
                    .updateStatus(status)
                    .then(response => {
                        if (response && !response.error) {
                            dispatch(setUser(response));
                            setLoading(false);
                        }
                    })
                    .catch(() => {
                        setLoading(false);
                    });
            }
        },
        [session.user, loading, api, networkError],
    );

    const initListener = () => {
        if (isThisUser && trackUserActivity) {
            const resetTimer = () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                updateStatus('online');
                timeoutRef.current = setTimeout(() => {
                    updateStatus('inactive');
                }, timeout);
            };

            window.addEventListener('click', resetTimer);
            window.addEventListener('mousemove', resetTimer);
            window.addEventListener('keydown', resetTimer);

            return () => {
                window.removeEventListener('click', resetTimer);
                window.removeEventListener('mousemove', resetTimer);
                window.removeEventListener('keydown', resetTimer);
            };
        }
    };

    useEffect(initListener, [
        isThisUser,
        trackUserActivity,
        updateStatus,
        timeout,
    ]);

    const status =
        session.user.id === user?.id ? session.user.status : user?.status;

    return useMemo(
        () => (
            <OnlineBadge
                status={status}
                badge={showBadge.toString()}
                ripple={showRipple.toString()}
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                size={size}
                variant="dot">
                {children}
            </OnlineBadge>
        ),
        [user, status],
    );
}
